import React, { useState, useMemo, useCallback } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import TableLoader from "../loaders/TableLoader";
import { Database, Save, TrendingUp, TrendingDown, Coins } from "lucide-react";
import { useSalaryConfigTable } from "@/hooks/useSalaryConfigTable";
import type { IBranchWiseSalaryHead } from "@/interface/salaryHead.interface";

export interface ISalaryHeadBackup {
    id: string;
    amount: string | number;
    salaryName?: string;
    userId: string;
    branchSalaryHeadId: string;
    branchSalaryHead?: {
        id: string;
        salaryName?: string;
        branchId?: string;
        salaryHeadId?: string;
        salaryHead?: {
            id: string;
            name: string;
            type?: string;
        };
    };
}

export interface ISalaryUserBackup {
    id: string;
    name: string;
    employeeCode?: string;
    salaries?: ISalaryHeadBackup[];
}

interface SalaryConfigTableProps {
    data: ISalaryUserBackup[];
    salaryHead?: IBranchWiseSalaryHead[]
    loading?: boolean;
    showCheckbox?: boolean;
    selectedItems?: Set<string | number>;
    onSelectAll?: (checked: boolean) => void;
    onSelectItem?: (item: ISalaryUserBackup, checked: boolean) => void;
    currentPage?: number;
    pageSize?: number;
    emptyMessage?: string;
    /** Number of columns from the left to make sticky (e.g., 3 for S.No, Code, Name) */
    stickyColumns?: number;
    onSaveUserSalary?: (
        userId: string,
        userSalary: { branchSalaryHeadId: string; amount: number }[]
    ) => Promise<void>;
    onSalaryChange?: (userId: string, headId: string, amount: number) => void;
    onViewUserDetails?: (user: ISalaryUserBackup) => void;
}

const SalaryConfigTable: React.FC<SalaryConfigTableProps> = ({
    data,
    salaryHead = [],
    loading = false,
    showCheckbox = false,
    selectedItems = new Set(),
    onSelectAll,
    onSelectItem,
    currentPage = 1,
    pageSize = 10,
    emptyMessage = "No salary configuration data available",
    stickyColumns = 5,
    onSaveUserSalary,
    onSalaryChange,
    onViewUserDetails,
}) => {

    const [savingMap, setSavingMap] = useState<Record<string, boolean>>({});

    const uniqueSalaryHeads = useMemo(() => {
        const isNetSalaryHead = (name: string): boolean => {
            const lower = name?.toLowerCase()?.trim() || "";
            return (
                lower.includes("net salary") ||
                lower.includes("net-salary") ||
                lower.includes("net_salary") ||
                lower === "net pay"
            );
        };

        const isGrossSalaryHead = (name: string): boolean => {
            const lower = name?.toLowerCase()?.trim() || "";
            return (
                lower.includes("gross salary") ||
                lower.includes("gross-salary") ||
                lower.includes("gross_salary") ||
                lower === "gross pay" ||
                lower === "gross"
            );
        };

        const isBasicHead = (name: string): boolean => {
            const lower = name?.toLowerCase()?.trim() || "";
            return lower.includes("basic");
        };

        const isEarningHead = (type?: string): boolean => {
            const lower = type?.toLowerCase()?.trim() || "";
            return lower === "earning" || lower === "allowance" || lower === "addition";
        };

        const isDeductionHead = (type?: string): boolean => {
            const lower = type?.toLowerCase()?.trim() || "";
            return lower === "deduction" || lower === "tax" || lower === "pf";
        };

        // 1. If parent provided configured branch salary heads (from API), use them directly
        if (salaryHead && salaryHead.length > 0) {
            const netHeads: typeof salaryHead = [];
            const grossHeads: typeof salaryHead = [];
            const basicHeads: typeof salaryHead = [];
            const earningHeads: typeof salaryHead = [];
            const otherHeads: typeof salaryHead = [];
            const deductionHeads: typeof salaryHead = [];

            salaryHead.forEach((h) => {
                const name = h.salaryName || h.salaryHead?.name || "";
                const type = h.salaryHead?.type;

                if (isNetSalaryHead(name)) {
                    netHeads.push(h);
                } else if (isGrossSalaryHead(name)) {
                    grossHeads.push(h);
                } else if (isBasicHead(name)) {
                    basicHeads.push(h);
                } else if (isEarningHead(type)) {
                    earningHeads.push(h);
                } else if (isDeductionHead(type)) {
                    deductionHeads.push(h);
                } else {
                    otherHeads.push(h);
                }
            });

            const sorted = [
                ...netHeads,
                ...grossHeads,
                ...basicHeads,
                ...earningHeads,
                ...otherHeads,
                ...deductionHeads,
            ];

            return sorted.map((h) => ({
                id: h.id,
                name: h.salaryName || h.salaryHead?.name || "Unnamed Head",
                type: (h.salaryHead?.type?.toLowerCase() || (isDeductionHead(h.salaryHead?.type) ? "deduction" : "earning")) as "earning" | "deduction",
            }));
        }

        // 2. Fallback: extract unique heads from data rows
        const headMap = new Map<string, { id: string; name: string; type: "earning" | "deduction" }>();

        data?.forEach((user) => {
            user?.salaries?.forEach((s) => {
                if (s?.branchSalaryHeadId && !headMap.has(s.branchSalaryHeadId)) {
                    const name = s.salaryName || s.branchSalaryHead?.salaryName || s.branchSalaryHead?.salaryHead?.name || "Unnamed Head";
                    const type = s.branchSalaryHead?.salaryHead?.type?.toLowerCase() === "deduction" ? "deduction" : "earning";
                    headMap.set(s.branchSalaryHeadId, {
                        id: s.branchSalaryHeadId,
                        name,
                        type,
                    });
                }
            });
        });

        const list = Array.from(headMap.values());
        const netList: typeof list = [];
        const grossList: typeof list = [];
        const basicList: typeof list = [];
        const earningList: typeof list = [];
        const otherList: typeof list = [];
        const deductionList: typeof list = [];

        list.forEach((h) => {
            if (isNetSalaryHead(h.name)) {
                netList.push(h);
            } else if (isGrossSalaryHead(h.name)) {
                grossList.push(h);
            } else if (isBasicHead(h.name)) {
                basicList.push(h);
            } else if (h.type === "earning") {
                earningList.push(h);
            } else if (h.type === "deduction") {
                deductionList.push(h);
            } else {
                otherList.push(h);
            }
        });

        return [
            ...netList,
            ...grossList,
            ...basicList,
            ...earningList,
            ...otherList,
            ...deductionList,
        ];
    }, [salaryHead, data]);

    const stickyOffsets = useMemo(() => {
        if (stickyColumns <= 0) return [];
        // Exact pixel widths matching TableHead/TableCell:
        // Checkbox (44px), S.No (54px), Emp Code (90px), Employee Name (140px), Salary Status (105px), Action (64px if onSaveUserSalary exists)
        const baseWidths: number[] = [];
        if (showCheckbox) baseWidths.push(44);
        baseWidths.push(54); // S.No
        baseWidths.push(90); // Emp Code
        baseWidths.push(140); // Employee Name
        baseWidths.push(105); // Salary Status
        if (onSaveUserSalary) baseWidths.push(64); // Action (Save) right next to Status!

        const headWidths = uniqueSalaryHeads ? uniqueSalaryHeads.map(() => 96) : [];
        const widths = [...baseWidths, ...headWidths];

        const offsets: number[] = [];
        let cumulative = 0;

        for (let i = 0; i < stickyColumns && i < widths.length; i++) {
            offsets.push(cumulative);
            cumulative += widths[i];
        }
        return offsets;
    }, [stickyColumns, uniqueSalaryHeads, showCheckbox, onSaveUserSalary]);

    const getStickyStyle = useCallback((colIndex: number, isHeader = false): React.CSSProperties | undefined => {
        if (stickyColumns <= 0 || colIndex >= stickyOffsets.length) return undefined;
        return {
            position: "sticky",
            left: stickyOffsets[colIndex],
            zIndex: isHeader ? 30 : 20,
        };
    }, [stickyColumns, stickyOffsets]);

    const getStickyClassName = useCallback((colIndex: number, _isHeader = false): string => {
        void _isHeader;
        if (stickyColumns <= 0 || colIndex >= stickyOffsets.length) return "";
        const isLastSticky = colIndex === stickyOffsets.length - 1;
        const borderShadow = isLastSticky ? "shadow-[3px_0_6px_-2px_rgba(0,0,0,0.15)] border-r border-slate-300" : "";
        return borderShadow;
    }, [stickyColumns, stickyOffsets]);

    // Handle client-side pagination fallback if data length exceeds pageSize
    const paginatedData = useMemo(() => {
        if (!data) return [];
        if (pageSize && data.length > pageSize) {
            const start = (currentPage - 1) * pageSize;
            return data.slice(start, start + pageSize);
        }
        return data;
    }, [data, currentPage, pageSize]);

    const isAllSelected = useMemo(() => {
        if (!paginatedData || paginatedData.length === 0) return false;
        return paginatedData.every((item) =>
            selectedItems?.has(String(item.id)) || selectedItems?.has(item.id)
        );
    }, [paginatedData, selectedItems]);

    // Custom hook managing input state and real-time Net Salary calculation per row
    const { inputs, handleInputChange } = useSalaryConfigTable(data, uniqueSalaryHeads);

    const handleSaveRow = async (userId: string) => {
        if (!onSaveUserSalary) return;
        const userHeadsMap = inputs[userId] || {};
        const payload = Object.entries(userHeadsMap).map(([branchSalaryHeadId, amount]) => ({
            branchSalaryHeadId,
            amount: Number(amount) || 0,
        }));

        setSavingMap((prev) => ({ ...prev, [userId]: true }));
        try {
            await onSaveUserSalary(userId, payload);
        } finally {
            setSavingMap((prev) => ({ ...prev, [userId]: false }));
        }
    };

    const totalColumns =
        (showCheckbox ? 1 : 0) + 4 + (onSaveUserSalary ? 1 : 0) + uniqueSalaryHeads.length;

    return (
        <TooltipProvider delayDuration={150}>
            <div className="text-card-foreground rounded-xl bg-white border border-border shadow-xs overflow-hidden space-y-1">
                {/* Header Legend Indicator Bar */}
                <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-3">
                        <span className="font-bold text-slate-700">Categories:</span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                            <TrendingUp className="w-3 h-3" /> Earning
                        </span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-rose-50 text-rose-700 border border-rose-200 font-semibold">
                            <TrendingDown className="w-3 h-3" /> Deduction
                        </span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
                            <Coins className="w-3 h-3" /> Net Salary
                        </span>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <Table className="min-w-full">
                        <TableHeader className="bg-slate-50 border-b border-slate-200">
                            <TableRow className="bg-muted/50 border-b-0">
                                {showCheckbox && (
                                    <TableHead
                                        className={`px-1 py-2 w-[44px] min-w-[44px] max-w-[44px] text-center bg-slate-50 ${getStickyClassName(0, true)}`}
                                        style={getStickyStyle(0, true)}
                                    >
                                        <input
                                            type="checkbox"
                                            aria-label="Select all"
                                            checked={isAllSelected}
                                            onChange={(e) => onSelectAll?.(e.target.checked)}
                                            className="h-3.5 w-3.5 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer accent-primary align-middle"
                                        />
                                    </TableHead>
                                )}
                                <TableHead
                                    className={`px-2 py-2 text-slate-800 text-[11px] font-bold uppercase w-[54px] min-w-[54px] max-w-[54px] text-center bg-slate-50 ${getStickyClassName(showCheckbox ? 1 : 0, true)}`}
                                    style={getStickyStyle(showCheckbox ? 1 : 0, true)}
                                >
                                    S.No
                                </TableHead>
                                <TableHead
                                    className={`px-2 py-2 text-slate-800 text-[11px] font-bold uppercase w-[90px] min-w-[90px] max-w-[90px] text-left bg-slate-50 ${getStickyClassName(showCheckbox ? 2 : 1, true)}`}
                                    style={getStickyStyle(showCheckbox ? 2 : 1, true)}
                                >
                                    Emp Code
                                </TableHead>
                                <TableHead
                                    className={`px-2 py-2 text-slate-800 text-[11px] font-bold uppercase w-[140px] min-w-[140px] max-w-[140px] text-left bg-slate-50 ${getStickyClassName(showCheckbox ? 3 : 2, true)}`}
                                    style={getStickyStyle(showCheckbox ? 3 : 2, true)}
                                >
                                    Employee Name
                                </TableHead>
                                <TableHead
                                    className={`px-2 py-2 text-slate-800 text-[11px] font-bold uppercase w-[105px] min-w-[105px] max-w-[105px] text-center bg-slate-50 ${getStickyClassName(showCheckbox ? 4 : 3, true)}`}
                                    style={getStickyStyle(showCheckbox ? 4 : 3, true)}
                                >
                                    Salary Status
                                </TableHead>
                                {onSaveUserSalary && (
                                    <TableHead
                                        className={`px-1 py-2 text-slate-800 text-[11px] font-bold uppercase text-center w-[64px] min-w-[64px] max-w-[64px] bg-slate-50 ${getStickyClassName(showCheckbox ? 5 : 4, true)}`}
                                        style={getStickyStyle(showCheckbox ? 5 : 4, true)}
                                    >
                                        Action
                                    </TableHead>
                                )}
                                {uniqueSalaryHeads?.map((head, headIdx) => {
                                    const colIdx = (showCheckbox ? 5 : 4) + (onSaveUserSalary ? 1 : 0) + headIdx;
                                    const lowerName = head.name.toLowerCase();
                                    const isNet =
                                        lowerName.includes("net salary") ||
                                        lowerName.includes("net-salary") ||
                                        lowerName.includes("net_salary") ||
                                        lowerName === "net pay";
                                    const isGross =
                                        lowerName.includes("gross salary") ||
                                        lowerName.includes("gross-salary") ||
                                        lowerName.includes("gross_salary") ||
                                        lowerName === "gross pay" ||
                                        lowerName === "gross";
                                    const isEarning = head.type === "earning";

                                    const headerBg = isNet
                                        ? "bg-amber-100 border-amber-500 text-amber-900"
                                        : isGross
                                            ? "bg-blue-100 border-blue-500 text-blue-900"
                                            : isEarning
                                                ? "bg-emerald-50/90 border-emerald-500 text-emerald-900"
                                                : "bg-rose-50/90 border-rose-500 text-rose-900";

                                    const badgeBg = isNet
                                        ? "bg-amber-200 text-amber-900 border-amber-400"
                                        : isGross
                                            ? "bg-blue-200 text-blue-900 border-blue-400"
                                            : isEarning
                                                ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                                : "bg-rose-100 text-rose-800 border-rose-300";

                                    const badgeText = isNet ? "Net" : isGross ? "Gross" : isEarning ? "Earn" : "Deduct";

                                    return (
                                        <TableHead
                                            key={head.id}
                                            className={`px-1 py-2 text-[11px] font-bold uppercase w-[96px] min-w-[96px] max-w-[96px] text-center border-b-2 ${headerBg} ${getStickyClassName(colIdx, true)}`}
                                            style={getStickyStyle(colIdx, true)}
                                        >
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div className="flex items-center justify-between gap-0.5 px-0.5 cursor-pointer select-none">
                                                        <span className="truncate text-[10px]">{head.name}</span>
                                                        <span className={`text-[8px] font-extrabold px-1 py-0.2 rounded uppercase border shrink-0 ${badgeBg}`}>
                                                            {badgeText}
                                                        </span>
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent side="top" className="bg-slate-900 text-white text-xs font-semibold px-2.5 py-1 rounded-md shadow-lg z-50">
                                                    <div className="flex items-center gap-1.5">
                                                        <span>{head.name}</span>
                                                        <span className="text-[10px] opacity-80 uppercase">({isNet ? "Net Pay" : isGross ? "Gross" : head.type})</span>
                                                    </div>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {loading ? (
                                <TableLoader columns={totalColumns} rows={5} />
                            ) : data?.length === 0 ? (
                                <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
                                    <TableCell colSpan={totalColumns} className="p-8 text-start align-start">
                                        <div className="flex flex-col items-center justify-start w-full">
                                            <Database className="h-8 w-8 text-muted-foreground" />
                                            <div className="mt-2 font-medium text-sm text-muted-foreground">
                                                {emptyMessage}
                                            </div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedData?.map((user, index) => {

                                    const isSaving = Boolean(savingMap[user.id]);

                                    return (
                                        <TableRow key={user.id || index} className="align-middle hover:bg-slate-50/80">
                                            {showCheckbox && (
                                                <TableCell
                                                    className={`px-1 py-1.5 w-[44px] min-w-[44px] max-w-[44px] text-center bg-white ${getStickyClassName(0)}`}
                                                    style={getStickyStyle(0)}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Select ${user.name || "employee"}`}
                                                        checked={selectedItems?.has(String(user.id)) || selectedItems?.has(user.id)}
                                                        onChange={(e) => onSelectItem?.(user, e.target.checked)}
                                                        className="h-3.5 w-3.5 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer accent-primary align-middle"
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell
                                                className={`px-2 py-1.5 text-xs font-medium text-slate-500 w-[54px] min-w-[54px] max-w-[54px] text-center bg-white ${getStickyClassName(showCheckbox ? 1 : 0)}`}
                                                style={getStickyStyle(showCheckbox ? 1 : 0)}
                                            >
                                                {String((currentPage - 1) * pageSize + index + 1).padStart(2, "0")}
                                            </TableCell>
                                            <TableCell
                                                className={`px-2 py-1.5 text-xs font-semibold text-slate-700 w-[90px] min-w-[90px] max-w-[90px] truncate text-left bg-white ${getStickyClassName(showCheckbox ? 2 : 1)}`}
                                                style={getStickyStyle(showCheckbox ? 2 : 1)}
                                            >
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onViewUserDetails?.(user);
                                                    }}
                                                    className={onViewUserDetails ? "font-bold text-primary hover:text-primary/80 hover:underline cursor-pointer" : ""}
                                                    title={onViewUserDetails ? "Click to view salary breakup" : undefined}
                                                >
                                                    {user.employeeCode || "—"}
                                                </span>
                                            </TableCell>
                                            <TableCell
                                                className={`px-2 py-1.5 text-xs font-semibold text-slate-800 w-[140px] min-w-[140px] max-w-[140px] truncate text-left bg-white ${getStickyClassName(showCheckbox ? 3 : 2)}`}
                                                style={getStickyStyle(showCheckbox ? 3 : 2)}
                                            >
                                                {user.name || "N/A"}
                                            </TableCell>
                                            <TableCell
                                                className={`px-2 py-1.5 text-center w-[105px] min-w-[105px] max-w-[105px] bg-white ${getStickyClassName(showCheckbox ? 4 : 3)}`}
                                                style={getStickyStyle(showCheckbox ? 4 : 3)}
                                            >
                                                {user.salaries && user.salaries.length > 0 ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
                                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                                        Configured
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 shadow-2xs">
                                                        <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                                                        Not Configured
                                                    </span>
                                                )}
                                            </TableCell>
                                            {onSaveUserSalary && (
                                                <TableCell
                                                    className={`px-1 py-1 text-center w-[64px] min-w-[64px] max-w-[64px] bg-white ${getStickyClassName(showCheckbox ? 5 : 4)}`}
                                                    style={getStickyStyle(showCheckbox ? 5 : 4)}
                                                >
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        onClick={() => handleSaveRow(user.id)}
                                                        disabled={isSaving}
                                                        className="h-6 px-2 bg-primary hover:bg-primary/90 text-white font-semibold text-[11px] rounded-md gap-1 shadow-2xs disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 disabled:cursor-not-allowed cursor-pointer"
                                                    >
                                                        <Save className="w-3 h-3" />
                                                        {isSaving ? "..." : "Save"}
                                                    </Button>
                                                </TableCell>
                                            )}
                                            {uniqueSalaryHeads.map((head, headIdx) => {
                                                const colIdx = (showCheckbox ? 5 : 4) + (onSaveUserSalary ? 1 : 0) + headIdx;
                                                const currentVal = inputs[user.id]?.[head.id] ?? 0;
                                                const lowerName = head.name.toLowerCase();
                                                const isNet =
                                                    lowerName.includes("net salary") ||
                                                    lowerName.includes("net-salary") ||
                                                    lowerName.includes("net_salary") ||
                                                    lowerName === "net pay";
                                                const isGross =
                                                    lowerName.includes("gross salary") ||
                                                    lowerName.includes("gross-salary") ||
                                                    lowerName.includes("gross_salary") ||
                                                    lowerName === "gross pay" ||
                                                    lowerName === "gross";

                                                const isStickyCol = colIdx < stickyOffsets.length;

                                                const cellBg = isStickyCol
                                                    ? (isNet ? "bg-amber-50 font-bold" : isGross ? "bg-blue-50 font-bold" : "bg-white font-medium")
                                                    : (isNet ? "bg-amber-50/40" : isGross ? "bg-blue-50/40" : "bg-white");

                                                const inputStyle = isNet
                                                    ? "bg-amber-100/60 border-amber-400 font-bold focus:border-amber-600 focus:ring-amber-600"
                                                    : isGross
                                                        ? "bg-blue-100/60 border-blue-400 font-bold focus:border-blue-600 focus:ring-blue-600"
                                                        : "bg-white border-slate-300 focus:border-primary focus:ring-primary text-slate-800 hover:border-slate-400";

                                                return (
                                                    <TableCell
                                                        key={`${user.id}-${head.id}`}
                                                        className={`px-1 py-1 w-fit max-w-full text-center ${cellBg} ${getStickyClassName(colIdx)}`}
                                                        style={getStickyStyle(colIdx)}
                                                    >
                                                        <Input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={currentVal}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                handleInputChange(user.id, head.id, val);
                                                                onSalaryChange?.(user.id, head.id, Number(val) || 0);
                                                            }}
                                                            maxLength={10}
                                                            className={`h-7 w-full text-xs font-semibold px-1 py-0.5 text-center border focus:ring-1 ${inputStyle}`}
                                                            style={
                                                                isNet
                                                                    ? { color: "var(--color-amber-600, #d97706)" }
                                                                    : isGross
                                                                        ? { color: "var(--color-blue-600, #2563eb)" }
                                                                        : undefined
                                                            }
                                                        />
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </TooltipProvider>
    );
};

export default SalaryConfigTable;